import { Alert, AlertIcon, AlertTitle, Button, Flex, FormControl, Grid, Image, Input, Select, Text, Textarea, useColorMode, useToast, VStack } from '@chakra-ui/react'
import Card from 'components/Card/Card'
import CardBody from 'components/Card/CardBody'
import CardHeader from 'components/Card/CardHeader'
import { useEffect, useState } from 'react'
import { sanitizeField } from 'functions/sanitizeForm'
import { useSubmitGlucoseMonitoring } from 'hooks/useGlucoseRegister'
import InputMask from "react-input-mask";

import logoHapvida from "assets/images/logo-hapvida.png"
import dateFormat, { stringToDate } from 'functions/dateFormat'
import { isAfter } from 'date-fns'

interface FormField {
    key: string,
    label: string,
    textarea?: boolean
    placeholder?: string
    options?: Option[]
}

interface Option {
    label: string
    value: string
}

interface FormData {
    key: string,
    value: string
}

const GlucoseMonitoring = () => {
    const params = new URLSearchParams(window.location.search);
    const cpf = params.get('cpf') || "";

    const [finished, setFinished] = useState<boolean>(false)

    const { setColorMode } = useColorMode()

    useEffect(() => {
        setColorMode("light");
    }, [])

    const toast = useToast()
    const [submitForm, { loading: loadingSubmit }] = useSubmitGlucoseMonitoring({
        onSuccess: (data: any) => {
            setFinished(true);
        },
        onError: () => {
            toast({
                id: "messageSent",
                title: `Ocorreu um erro ao cadastrar`,
                duration: 4000,
                position: "bottom",
                status: "error"
            })
        }
    });

    const [submitFormAndRefresh, { loading: loadingSubmitRefresh }] = useSubmitGlucoseMonitoring({
        onSuccess: (data: any) => {
            window.location.reload();
        },
        onError: () => {
            toast({
                id: "messageSent",
                title: `Ocorreu um erro ao cadastrar`,
                duration: 4000,
                position: "bottom",
                status: "error"
            })
        }
    });

    const isLoading = loadingSubmitRefresh || loadingSubmit;

    const today =  dateFormat(new Date(), "dd/MM/yyyy");

    const [formData, setFormData] = useState({
        cpf: cpf,
        glucose_value: "",
        date: today,
        time: "",
        status: "",
        gestational_age: "",
        weight: "",
        observation: ""
    })

    const allowDecimal = (value: string) => {
        let sanitized = value.replace(/[^0-9.,]/g, '');
      
        const hasDot = sanitized.includes('.');
        const hasComma = sanitized.includes(',');
      
        if (hasDot && hasComma) {
          const firstSeparatorIndex = Math.min(
            sanitized.indexOf('.'),
            sanitized.indexOf(',')
          );
      
          sanitized =
            sanitized.slice(0, firstSeparatorIndex + 1) +
            sanitized
              .slice(firstSeparatorIndex + 1)
              .replace(/[.,]/g, '');
        }
      
        return sanitized;
      };

    const allFieldsFilled = (formData: any) => 
        formData.cpf != "" && formData.cpf != null &&
        formData.glucose_value != "" && formData.glucose_value != null &&
        formData.date != "" && formData.date != null &&
        formData.time != "" && formData.time != null &&
        formData.status != "" && formData.status != null &&
        formData.gestational_age != "" && formData.gestational_age != null

    const onSubmit = (refresh = false) => {
        formData.cpf = sanitizeField(formData.cpf);

        if(formData.date.length == 10) {
            const realDate = stringToDate(formData.date);

            if(!allFieldsFilled(formData)) {
                toast({
                    id: "messageSent",
                    title: "Por favor, preencha todos os campos obrigatórios.",
                    duration: 4000,
                    position: "bottom",
                    status: "warning"
                })

                return;
            }
            
            if(isAfter(realDate, new Date())) {
                toast({
                    id: "invalidDate",
                    title: "Data informada não pode ser maior que hoje",
                    duration: 4000,
                    position: "bottom",
                    status: "warning"
                })

                return;
            }
        
        }

        const variables = {
            input: {
                ...formData,
            }
        }

        if(!refresh)
            submitForm({ variables });
        else
            submitFormAndRefresh({ variables });
    }

    return (
        <Flex w="full" minH="100vh" justifyContent="center" alignItems="center">
            <Card maxW="420px" minH="460px">
                <CardHeader minH="80px" justifyContent="center" alignItems="center" mx="auto">
                    <VStack w="full" h="full">
                        <Image src={logoHapvida} maxW="200px" h="auto" />
                        <Text align="center">Monitoramento Glicêmico para Diabetes Gestacional</Text>
                    </VStack>
                </CardHeader>
                <CardBody mt="auto" mb="auto">
                    <Flex flexDirection="column" w="full">
                        {
                            finished ?
                                <Flex w="full" justifyContent="center" alignItems="center">
                                    <Alert
                                        status='success'
                                        variant='subtle'
                                        flexDirection='column'
                                        alignItems='center'
                                        justifyContent='center'
                                        textAlign='center'
                                        height='200px'
                                    >
                                        <AlertIcon boxSize='40px' mr={0} />
                                        <AlertTitle mt={4} mb={1} fontSize='md'>
                                        <Text>Obrigado pelo preenchimento das informações.</Text>
                                        <Text>Lembre-se sempre de acessar o mesmo link para registrar outros valores, conforme orientação.</Text>
                                        </AlertTitle>
                                    </Alert>
                                </Flex>
                                :
                                <>
                                    <Grid
                                        w="full"
                                        templateColumns="repeat(1, 1fr)"
                                        templateRows="repeat(2, 1fr)"
                                        gap="16px"
                                        mb="8"
                                    >
                                        <FormControl hidden={true}>
                                            <Input
                                                defaultValue={cpf}
                                                value={formData.cpf}
                                                onChange={({ target }) => setFormData({ ...formData, cpf: target.value })}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Valor da Glicemia (mg/dL) *</Text>
                                            <Input
                                                value={formData.glucose_value}
                                                onChange={({ target }) => setFormData({ ...formData, glucose_value: allowDecimal(target.value) })}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Data *</Text>
                                            <Input
                                                value={formData.date}
                                                onChange={({ target }) => setFormData({ ...formData, date: target.value })}
                                                as={InputMask}
                                                mask={"99/99/9999"}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Horário *</Text>
                                            <Input
                                                value={formData.time}
                                                onChange={({ target }) => setFormData({ ...formData, time: target.value })}
                                                as={InputMask}
                                                mask={"99:99"}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Situação *</Text>
                                            <Select value={formData.status} placeholder="Selecione uma opção..." onChange={({ target }) => setFormData({ ...formData, status: target.value })}>
                                                <option value="Em Jejum">Em Jejum</option>
                                                <option value="1h Pós café">1h Pós café</option>
                                                <option value="Antes do almoço">Antes do almoço</option>
                                                <option value="1h Pós almoço">1h Pós almoço</option>
                                                <option value="Antes do jantar">Antes do jantar</option>
                                                <option value="1h Pós jantar">1h Pós jantar</option>
                                                <option value="Madrugada">Madrugada</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Idade Gestacional atualizada (em semanas) *</Text>
                                            <Input
                                                value={formData.gestational_age}
                                                onChange={({ target }) => setFormData({ ...formData, gestational_age: allowDecimal(target.value) })}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Qual o seu peso? (em kg)</Text>
                                            <Input
                                                value={formData.weight}
                                                onChange={({ target }) => setFormData({ ...formData, weight: allowDecimal(target.value) })}
                                                borderRadius="8px"
                                                fontSize="md"
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <Text mb={2} fontWeight="bold" fontSize="md">Observação</Text>
                                            <Textarea
                                                value={formData.observation}
                                                onChange={({ target }) => setFormData({ ...formData, observation: target.value })}
                                                borderRadius="8px"
                                                fontSize="md"
                                                minH={100}
                                                placeholder="Registre aqui observações relacionadas ao uso de medicações, dieta, ganho de peso ou demais informações que julgar pertinente repassar para a equipe de saúde sobre a data informada, tais como: alimentação, uso de alguma medicação, ou qualquer outro dado."
                                            />
                                        </FormControl>
                                        <Text fontSize="sm">* Campos obrigatórios</Text>
                                    </Grid>
                                    <Flex w="full" mb="2" direction="column" gap={2}>
                                        <Button
                                            isLoading={isLoading}
                                            disabled={isLoading}
                                            w="full"
                                            colorScheme="blue"
                                            onClick={() => onSubmit(true)}
                                        >Registrar e informar outro valor</Button>
                                        <Button
                                            isLoading={isLoading}
                                            disabled={isLoading}
                                            w="full"
                                            colorScheme="blue"
                                            onClick={() => onSubmit()}
                                        >Registrar e finalizar</Button>
                                    </Flex>
                                </>
                        }
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    )
}

export default GlucoseMonitoring